import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUniversity,
  FaHandHoldingUsd,
  FaCoins,
  FaShieldAlt,
  FaExchangeAlt,
  FaFingerprint,
  FaMoneyCheckAlt,
  FaChild,
  FaBriefcase,
  FaStore,
  FaGlobe,
  FaHospital,
  FaCar,
  FaCreditCard,
  FaMoneyBillWave,
  FaTags,
  FaPiggyBank,
} from "react-icons/fa";

function ServicesPage() {
  const services = [
    {
      name: "Savings Accounts",
      description:
        "Securely manage your finances with our range of savings account options tailored to your needs. Earn interest while enjoying easy access to your funds.",
      icon: <FaUniversity />,
      link: "/savings-accounts",
      category: "banking",
    },
    {
      name: "Gold Loan Services",
      description:
        "Unlock the value of your gold assets with our hassle-free gold loan services. Get quick access to funds at competitive interest rates.",
      icon: <FaCoins />,
      link: "/gold-loans",
      category: "loans",
    },
    {
      name: "Micro ATM (Card Swipe)",
      description:
        "Conduct convenient banking transactions, including cash withdrawals and deposits, using our Micro ATM card swipe facility.",
      icon: <FaHandHoldingUsd />,
      link: "/micro-atm",
      category: "banking",
    },
    {
      name: "AEPS",
      description:
        "Access your bank account and perform transactions securely using your Aadhaar number and biometric authentication through our AEPS service.",
      icon: <FaFingerprint />,
      link: "/aeps",
      category: "banking",
    },
    {
      name: "Fino Domestic Money Transfer",
      description:
        "Transfer funds instantly and securely to any bank account in India with our Fino Domestic Money Transfer service.",
      icon: <FaExchangeAlt />,
      link: "/fino-domestic-money-transfer",
      category: "payments",
    },
    {
      name: "Life Insurance",
      description:
        "Protect your loved ones and secure their financial future with our comprehensive life insurance plans, offering coverage against various risks.",
      icon: <FaShieldAlt />,
      link: "/life-insurance",
      category: "insurance",
    },
    {
      name: "Current Accounts",
      description:
        "Enjoy the flexibility and convenience of our current account services, designed to meet the dynamic needs of your business.",
      icon: <FaMoneyCheckAlt />,
      link: "/current-accounts",
      category: "banking",
    },
    {
      name: "Minor Accounts",
      description:
        "Introduce your children to the world of banking with our minor accounts, designed to teach them financial responsibility from a young age.",
      icon: <FaChild />,
      link: "/minor-accounts",
      category: "banking",
    },
    {
      name: "Personal Loans",
      description:
        "Fulfill your personal aspirations with our flexible personal loan options. Whether it's for education, travel, or a medical emergency, we've got you covered.",
      icon: <FaHandHoldingUsd />,
      link: "/personal-loans",
      category: "loans",
    },
    {
      name: "Business Loans",
      description:
        "Fuel your business growth with our tailored business loan solutions. We offer term loans, working capital loans, and other financing options to meet your specific needs.",
      icon: <FaBriefcase />,
      link: "/business-loans",
      category: "loans",
    },
    {
      name: "Merchant Services",
      description:
        "Empower your business to accept digital payments with our comprehensive merchant services, including POS machines and payment gateways.",
      icon: <FaStore />,
      link: "/merchant-services",
      category: "business",
    },
    {
      name: "Fino International Money Transfer",
      description:
        "Send money abroad conveniently and securely with our Fino International Money Transfer service, offering competitive exchange rates and fast processing.",
      icon: <FaGlobe />,
      link: "/fino-international-money-transfer",
      category: "payments",
    },
    {
      name: "Health Insurance",
      description:
        "Safeguard your health and well-being with our health insurance plans, providing coverage for medical expenses and hospitalization.",
      icon: <FaHospital />,
      link: "/health-insurance",
      category: "insurance",
    },
    {
      name: "General Insurance",
      description:
        "Protect your valuable assets, including your home, vehicle, and business, with our wide range of general insurance products.",
      icon: <FaCar />,
      link: "/general-insurance",
      category: "insurance",
    },
    {
      name: "Credit Card Bill Payments",
      description:
        "Pay your credit card bills conveniently through our platform. We support all major credit card providers.",
      icon: <FaCreditCard />,
      link: "/credit-card-bill-payments",
      category: "payments",
    },
    {
      name: "Atal Pension Yojana",
      description:
        "Secure your future with the Atal Pension Yojana, a government-backed pension scheme for the unorganized sector.",
      icon: <FaMoneyBillWave />,
      link: "/atal-pension-yojana",
      category: "banking",
    },
    {
      name: "FASTags",
      description:
        "Enjoy cashless travel on national highways with our FASTag services. Recharge your FASTag quickly and easily through our platform.",
      icon: <FaTags />,
      link: "/fastags",
      category: "payments",
    },
    {
      name: "Fixed Deposits",
      description:
        "Grow your savings with our attractive fixed deposit schemes. Enjoy competitive interest rates and flexible tenures.",
      icon: <FaPiggyBank />,
      link: "/fixed-deposits",
      category: "banking",
    },
  ];

  const [selectedCategory, setSelectedCategory] = useState(null);

  const filteredServices = selectedCategory
    ? services.filter((service) => service.category === selectedCategory)
    : services;

  const categories = [
    { name: "All", value: null },
    { name: "Banking", value: "banking" },
    { name: "Loans", value: "loans" },
    { name: "Payments", value: "payments" },
    { name: "Insurance", value: "insurance" },
    { name: "Business", value: "business" },
  ];

  return (
    <section className="py-20 md:py-28 px-4 md:px-8 bg-gray-50">
      <div className="container mx-auto">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-8 md:mb-12 text-center">
          Our Services
        </h1>

        <div className="mb-8 md:mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">
            Find Services for You
          </h2>
          <div className="flex flex-wrap justify-center space-x-4">
            {categories.map((category) => (
              <button
                key={category.value}
                className={`px-6 py-3 rounded-full transition duration-300 ease-in-out font-bold ${
                  selectedCategory === category.value
                    ? "bg-blue-600 text-white"
                    : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                }`}
                onClick={() => setSelectedCategory(category.value)}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
          <AnimatePresence>
            {filteredServices.map((service, index) => (
              <motion.div
                key={service.name}
                className="bg-white p-6 md:p-8 rounded-xl shadow-lg flex flex-col items-center transition duration-500 ease-in-out transform hover:-translate-y-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                exit={{ opacity: 0 }}
              >
                <div className="mb-6 text-blue-600 text-5xl">
                  {service.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-800 mb-4 text-center">
                  {service.name}
                </h3>
                <p className="text-gray-600 text-center mb-6">
                  {service.description}
                </p>
                <Link
                  to={service.link}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-full transition duration-300 ease-in-out mt-auto"
                >
                  Learn More
                </Link>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}

export default ServicesPage;